<template>
  <div class="travel-body" :class="{ 'travel-body--customer': channel === 'customer' }">
      <header class="travel-header">
        <div class="travel-header-left">
          <h2 class="page-title">
            <v-icon class="tw-cursor-pointer tw-mr-2" color="#1E4276" @click="goBack">mdi-arrow-left</v-icon>
            {{ travelConfig.labels.planTitle }}
          </h2>
          <p class="page-sub">{{ travelConfig.labels.planSub }}</p>
        </div>
        <button type="button" class="btn-new-sale" @click="showNewQuoteConfirm = true">
          <v-icon x-small color="#1E4276" class="tw-mr-1">mdi-refresh</v-icon>
          New Quote
        </button>
      </header>

      <customer-agent-picker v-if="channel === 'customer' && !guest" />

      <v-dialog v-model="showNewQuoteConfirm" max-width="380" overlay-opacity="0.5">
        <div class="new-sale-dialog">
          <div class="nsd-header">
            <div class="nsd-icon">
              <v-icon size="24" color="white">mdi-alert-circle-outline</v-icon>
            </div>
            <div>
              <p class="nsd-title">Start a New Quote?</p>
              <p class="nsd-sub">Travel insurance</p>
            </div>
          </div>
          <div class="nsd-body">
            <p class="nsd-desc">Unsaved details in this session will be removed. Saved quotes remain in the system.</p>
          </div>
          <div class="nsd-footer">
            <button type="button" class="nsd-btn-cancel" @click="showNewQuoteConfirm = false">Keep Working</button>
            <button type="button" class="nsd-btn-confirm" @click="startNewQuote">
              <v-icon x-small color="white" class="tw-mr-1">mdi-refresh</v-icon>
              Yes, Start New
            </button>
          </div>
        </div>
      </v-dialog>

      <div class="quote-container">
        <div class="quote-layout">
          <div class="quote-form-column">
            <div class="section-card">
              <div class="section-header">
                <span class="section-number">1</span>
                <h3>Trip details</h3>
              </div>

              <div class="pa-form-fields">
              <div v-if="channel === 'customer' && !guest" class="cover-for-block">
                <label class="input-label">Are you travelling on this trip?</label>
                <div class="cover-for-options" role="radiogroup" aria-label="Are you travelling on this trip?">
                  <button
                    type="button"
                    class="cover-for-option"
                    :class="{ active: accountHolderTravelling }"
                    @click="setAccountHolderTravelling(true)"
                  >
                    <v-icon small :color="accountHolderTravelling ? '#1E4276' : '#94A3B8'">mdi-account</v-icon>
                    Yes
                  </button>
                  <button
                    type="button"
                    class="cover-for-option"
                    :class="{ active: !accountHolderTravelling }"
                    @click="setAccountHolderTravelling(false)"
                  >
                    <v-icon small :color="!accountHolderTravelling ? '#1E4276' : '#94A3B8'">mdi-account-arrow-right</v-icon>
                    No
                  </button>
                </div>
                <p v-if="accountHolderTravelling" class="field-hint">
                  You're included as traveller 1. Add anyone else joining you below.
                </p>
                <p v-else class="field-hint">
                  You'll be the policyholder. Enter each person who is travelling — not yourself.
                </p>
                <p v-if="loadingAccountHolder" class="field-hint">
                  Loading your details…
                </p>
                <p v-else-if="accountHolderDobError && !(accountHolderTravelling && travellerDobAt(0))" class="field-error">
                  {{ accountHolderDobError }}
                </p>
              </div>

              <div class="input-group">
                <label class="input-label">Product</label>
                <v-select
                  v-model="selectedProdCode"
                  :items="productOptions"
                  item-text="label"
                  item-value="prodCode"
                  outlined dense hide-details="auto"
                  class="custom-input"
                  placeholder="Select product"
                />
                <span v-if="isFamilyPlan" class="family-plan-note">
                  <v-icon size="14" color="#1E4276">mdi-information-outline</v-icon>
                  Family cover plans holds up Policy holder, spouse and a max of 3 children
                </span>
              </div>

              <div class="input-group">
                <label class="input-label">Destination country</label>
                <v-autocomplete
                  v-model="destinationCountry"
                  :items="countryOptions"
                  item-text="label"
                  item-value="code"
                  outlined dense hide-details="auto"
                  class="custom-input"
                  placeholder="Search country"
                  :disabled="!selectedProduct"
                />
              </div>

              <div class="form-row">
                <div class="input-group">
                  <label class="input-label">Start date</label>
                  <v-text-field
                    v-model="startDate"
                    type="date"
                    :min="minStartDate"
                    outlined
                    dense
                    hide-details="auto"
                    class="custom-input"
                  />
                </div>
                <div class="input-group">
                  <label class="input-label">End date</label>
                  <v-text-field
                    v-model="endDate"
                    type="date"
                    :min="minEndDate"
                    outlined
                    dense
                    hide-details="auto"
                    class="custom-input"
                  />
                </div>
              </div>
              <p v-if="dateError" class="field-error">{{ dateError }}</p>

              <div class="input-group">
                <label class="input-label">Number of travellers</label>
                <p class="field-hint">{{ travellerCountHint }}</p>
                <div class="traveller-stepper">
                  <button
                    type="button"
                    class="stepper-btn"
                    :disabled="travellerCount <= 1"
                    aria-label="Fewer travellers"
                    @click="setTravellerCount(travellerCount - 1)"
                  >
                    <v-icon size="18" color="#1E4276">mdi-minus</v-icon>
                  </button>
                  <span class="stepper-value">{{ travellerCount }}</span>
                  <button
                    type="button"
                    class="stepper-btn"
                    :disabled="travellerCount >= maxTravellers"
                    aria-label="More travellers"
                    @click="setTravellerCount(travellerCount + 1)"
                  >
                    <v-icon size="18" color="#1E4276">mdi-plus</v-icon>
                  </button>
                </div>
              </div>

              <div
                v-if="showCustomerPolicyholderDob"
                class="policyholder-dob-block"
              >
                <label class="input-label">
                  Your date of birth (policyholder) <span class="req">*</span>
                </label>
                <p class="field-hint">
                  You're not travelling — we need your date of birth as the adult policyholder (18+).
                </p>
                <v-progress-linear
                  v-if="loadingAccountHolder"
                  indeterminate
                  color="#00AEEF"
                  class="tw-mb-2"
                />
                <v-text-field
                  :value="accountHolderDob"
                  type="date"
                  :max="maxPolicyholderDob"
                  :readonly="policyholderDobReadonly"
                  outlined
                  dense
                  hide-details="auto"
                  class="custom-input"
                  @input="onAccountHolderDobChange"
                  @change="onAccountHolderDobChange"
                />
                <p v-if="policyholderDobReadonly" class="field-hint">
                  Loaded from your Pacis account.
                </p>
                <p v-if="accountHolderDobError" class="field-error">{{ accountHolderDobError }}</p>
              </div>

              <div class="input-group">
                <label class="input-label">Customer email <span class="optional">(optional — for quote email)</span></label>
                <v-text-field
                  v-model="customerEmail"
                  type="email"
                  outlined dense hide-details="auto"
                  class="custom-input"
                  placeholder="customer@email.com"
                />
              </div>

              <div
                v-if="showSelfTravellerDob"
                class="dob-card self-dob-card"
              >
                <div class="dob-card-top">
                  <span class="traveller-badge">Traveller 1 (You)</span>
                </div>
                <div class="input-group">
                  <label class="input-label">Your date of birth <span class="req">*</span></label>
                  <v-progress-linear
                    v-if="loadingAccountHolder"
                    indeterminate
                    color="#00AEEF"
                    class="tw-mb-2"
                  />
                  <v-text-field
                    :value="travellerDobAt(0)"
                    type="date"
                    :max="maxPolicyholderDob"
                    :readonly="selfDobLockedFromIprs"
                    outlined
                    dense
                    :hide-details="!selfTravellerDobError"
                    class="custom-input"
                    :error-messages="selfTravellerDobError ? [selfTravellerDobError] : []"
                    :error="!!selfTravellerDobError"
                    @input="onTravellerDobInput(0, $event)"
                    @change="onTravellerDobInput(0, $event)"
                  />
                </div>
              </div>

              <travel-traveller-dob-form
                v-if="!accountHolderChannel && travellerCount >= 1"
                :travellers="quoteTravellers"
                :count="travellerCount"
                @update:travellers="onQuoteTravellersUpdate"
                @policyholder-error="onPolicyholderError"
              />

              <div
                v-if="showSingleTravellerDob"
                class="dob-card self-dob-card"
              >
                <div class="dob-card-top">
                  <span class="traveller-badge">Traveller 1</span>
                </div>
                <div class="input-group">
                  <label class="input-label">Date of birth <span class="req">*</span></label>
                  <v-text-field
                    :value="travellerDobAt(0)"
                    type="date"
                    :max="maxPolicyholderDob"
                    outlined
                    dense
                    hide-details="auto"
                    class="custom-input"
                    @input="onTravellerDobInput(0, $event)"
                    @change="onTravellerDobInput(0, $event)"
                  />
                  <p
                    v-if="travellerDobAt(0) && isTravelMinor(travellerDobAt(0))"
                    class="field-hint"
                  >
                    Under 18 — premium is priced for their age. Name and passport are collected on the next step.
                  </p>
                </div>
              </div>

              <travel-traveller-dob-form
                v-if="showTravellerDobForm"
                :travellers="dobFormTravellers"
                :count="dobFormCount"
                :self-cover="false"
                :traveller-index-offset="customerTravellerDobOffset"
                @update:travellers="onDobFormTravellersUpdate"
                @policyholder-error="onPolicyholderError"
              />

              <div v-if="showManualPolicyholderDob" class="policyholder-dob-block">
                <label class="input-label">
                  Adult policyholder date of birth <span class="req">*</span>
                </label>
                <p class="field-hint">
                  At least one traveller is under 18. Enter the date of birth of an adult policyholder.
                </p>
                <v-text-field
                  :value="accountHolderDob"
                  type="date"
                  :max="maxPolicyholderDob"
                  outlined dense hide-details="auto"
                  class="custom-input"
                  @input="onAccountHolderDobChange"
                  @change="onAccountHolderDobChange"
                />
                <p v-if="accountHolderDobError" class="field-error">{{ accountHolderDobError }}</p>
              </div>
              </div>
            </div>
          </div>

          <aside class="quote-sidebar">
            <travel-quote-sidebar
              :gross-usd="grossUsd"
              :fx-rate="fxRate"
              :summary-lines="summaryLines"
              :quote-snapshot="activeQuote"
              :price-calculated="!!grossUsd"
              :pricing="pricing"
              :syncing="syncing"
              :continue-label="sidebarContinueLabel"
              :gate-reason="quoteGateReason"
              :quote-ready="canQuote"
              :auto-price="true"
              @continue="goToSale"
            />
          </aside>
        </div>
      </div>
    </div>
</template>

<script>
import TravelQuoteSidebar from '@/components/agent/travel/TravelQuoteSidebar';
import TravelTravellerDobForm from '@/components/agent/travel/TravelTravellerDobForm';
import CustomerAgentPicker from '@/components/shared/CustomerAgentPicker';
import {
  TRAVEL_AGENT_PRODUCTS, findTravelProduct, ZONE_LABELS, DEFAULT_USD_TO_KSH,
} from '@/constants/travelProducts';
import { getCountriesForRegion, formatCountryOption } from '@/constants/travelCountries';
import {
  applyIprsToTravellerRow,
  buildMapfreTravelPayload,
  buildPlanStageInsureds,
  buildQuoteInsureds,
  coverForFromAccountHolderTravelling,
  emptyQuoteTraveller,
  durationLabelFromDates,
  inferAccountHolderTravelling,
  minTravelStartDate,
  travelDateRangeError,
  travelDaysBetween,
  isTravelAdult,
  needsSeparateAdultPolicyholder,
  isTravelMinor,
  normalizeTravelDob,
  reconcileTravelPolicyholder,
  usdToKsh,
  validateTravelPolicyholder,
} from '@/utils/travelPricing';
import agentTravelApi, {
  AGENT_TRAVEL_QUOTES_API_ENABLED,
  generateLocalTravelQuoteId,
  isRemoteTravelQuoteId,
} from '@/services/agentTravelApi';
import {
  referralAgencyNumericId,
  referralAgentShortDesc,
} from '@/services/referralApi';
import {
  saveTravelPendingQuote,
  loadTravelPendingQuote,
  clearTravelSession,
} from '@/utils/saleCache';
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { trackAgentInteraction } from '@/utils/agentAnalytics';
import { getTravelChannelConfig } from '@/utils/travelChannel';
import { getCognitoIdentityProfile } from '@/utils/paChannel';
import { fetchIprsProfile } from '@/utils/motorIdentityVerify';
import {
  GUEST_SESSION_KEY,
  GUEST_QUOTE_ROUTES,
  guestBuyRouteForProduct,
  redirectGuestToBuy,
} from '@/utils/guestQuote';

const PRICE_DEBOUNCE_MS = 600;

export default {
  name: 'TravelPlanSelectPage',
  components: { TravelQuoteSidebar, TravelTravellerDobForm, CustomerAgentPicker },
  props: {
    channel: {
      type: String,
      required: true,
      validator: (v) => v === 'agent' || v === 'customer',
    },
    guest: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedProdCode: 'EUR_BAS',
      destinationCountry: null,
      startDate: '',
      endDate: '',
      fxRate: DEFAULT_USD_TO_KSH,
      loadingFxRate: false,
      fxRateError: '',
      travellerCount: 1,
      quoteTravellers: [emptyQuoteTraveller(false)],
      accountHolderTravelling: false,
      accountHolderDob: '',
      accountHolderFirstName: '',
      accountHolderSurname: '',
      loadingAccountHolder: false,
      accountHolderDobError: '',
      accountHolderIprsFetched: false,
      customerEmail: '',
      grossUsd: null,
      pricingData: null,
      pricing: false,
      syncing: false,
      activeQuote: {},
      agnShtDesc: 'DIRECT',
      agentNumericId: 0,
      priceTimer: null,
      priceRequestId: 0,
      lastPricingError: '',
      showNewQuoteConfirm: false,
    };
  },
  created() {
    if (!this.guest && this.channel === 'customer') {
      this.accountHolderTravelling = true;
      this.quoteTravellers = [emptyQuoteTraveller(true)];
    }
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser']),
    ...mapGetters('referral', ['referralAgent', 'hasValidReferral']),
    /** Signed-in customer, who says whether they travel; agents and guests only enter the travellers. */
    accountHolderChannel() {
      return this.channel === 'customer' && !this.guest;
    },
    /** Whether the policyholder is on the trip: agents and guests need a separate one only when every traveller is a minor. */
    quoteAccountHolderTravelling() {
      return this.accountHolderChannel ? this.accountHolderTravelling : !this.usesSeparatePolicyholderAtQuote;
    },
    travelConfig() {
      return getTravelChannelConfig({ travelChannel: this.channel });
    },
    journey() {
      return this.$route.query.journey || this.travelConfig.defaultJourney;
    },
    productOptions() {
      return TRAVEL_AGENT_PRODUCTS;
    },
    selectedProduct() {
      return findTravelProduct(this.selectedProdCode);
    },
    isFamilyPlan() {
      return this.selectedProduct?.planType === 'family';
    },
    /** Family plans: policyholder + spouse + max 3 children = 5 travellers. */
    maxTravellers() {
      return this.isFamilyPlan ? 5 : 10;
    },
    countryOptions() {
      if (!this.selectedProduct) return [];
      return getCountriesForRegion(this.selectedProduct.region).map(formatCountryOption);
    },
    minStartDate() {
      return minTravelStartDate();
    },
    minEndDate() {
      const today = minTravelStartDate();
      if (this.startDate && this.startDate >= today) return this.startDate;
      return today;
    },
    dateError() {
      if (!this.startDate && !this.endDate) return '';
      return travelDateRangeError(this.startDate, this.endDate);
    },
    quoteDobsComplete() {
      const today = new Date().toISOString().slice(0, 10);
      return Array.from({ length: this.travellerCount }, (_, i) => i).every((i) => {
        const dob = this.travellerDobAt(i);
        return dob && dob <= today;
      });
    },
    canQuote() {
      return !!(
        this.selectedProduct
        && this.destinationCountry
        && this.startDate
        && this.endDate
        && !this.dateError
        && this.travellerCount >= 1
        && this.travellerCount <= this.maxTravellers
        && !this.loadingAccountHolderBlocksQuote
        && !this.loadingFxRate
        && !this.fxRateError
        && Number(this.fxRate) > 0
        && this.quoteDobsComplete
        && this.policyholderReadyForQuote
      );
    },
    /** Changes when all inputs required for Mapfre getPrice are present. */
    quoteReadyKey() {
      const dobs = Array.from({ length: this.travellerCount }, (_, i) => this.travellerDobAt(i));
      let travellingFlag = '';
      if (this.accountHolderChannel) {
        travellingFlag = this.accountHolderTravelling ? '1' : '0';
      }
      const inputs = [
        this.selectedProdCode,
        this.destinationCountry,
        this.startDate,
        this.endDate,
        this.travellerCount,
        travellingFlag,
        this.effectiveAccountHolderDob,
        this.accountHolderDob,
        ...dobs,
      ];
      if (this.channel === 'agent') {
        inputs.push(this.agnShtDesc);
      }
      if (!this.canQuote) return '';
      return inputs.join('|');
    },
    /** Debounced repricing when any pricing input changes (incl. before form is complete). */
    pricingInputsKey() {
      const dobs = Array.from({ length: this.travellerCount }, (_, i) => this.travellerDobAt(i));
      return [
        this.selectedProdCode,
        this.destinationCountry,
        this.startDate,
        this.endDate,
        this.travellerCount,
        this.accountHolderDob,
        this.agnShtDesc,
        ...dobs,
      ].join('|');
    },
    loadingAccountHolderBlocksQuote() {
      if (!this.loadingAccountHolder || !this.accountHolderChannel) return false;
      if (this.accountHolderTravelling) return !this.travellerDobAt(0);
      return !this.effectiveAccountHolderDob;
    },
    quoteGateReason() {
      if (!this.selectedProduct) return 'Select a product to continue.';
      if (!this.destinationCountry) return 'Select a destination country.';
      if (this.dateError) return this.dateError;
      if (!this.startDate || !this.endDate) return 'Enter your travel dates.';
      if (this.loadingAccountHolderBlocksQuote) return 'Loading your profile…';
      if (!this.quoteDobsComplete) return 'Enter date of birth for each traveller.';
      if (!this.policyholderReadyForQuote) {
        if (this.accountHolderChannel && !this.accountHolderTravelling) {
          return 'Enter your date of birth as policyholder (must be 18+).';
        }
        if (this.showManualPolicyholderDob) {
          return 'Enter adult policyholder date of birth (must be 18+).';
        }
        return 'Policyholder must be at least 18 years old.';
      }
      if (this.fxRateError) return this.fxRateError;
      if (Number(this.fxRate) <= 0) return 'Exchange rate unavailable.';
      return '';
    },
    policyholderReadyForQuote() {
      if (this.accountHolderChannel) {
        if (this.accountHolderTravelling) {
          const mainDob = this.travellerDobAt(0);
          return !!mainDob && isTravelAdult(mainDob);
        }
        const holderDob = this.effectiveAccountHolderDob;
        if (!holderDob || !isTravelAdult(holderDob)) return false;
        return true;
      }
      if (this.showManualPolicyholderDob) {
        return !!this.accountHolderDob && isTravelAdult(this.accountHolderDob);
      }
      const insureds = buildQuoteInsureds(reconcileTravelPolicyholder(this.quoteTravellersForPricing()));
      return validateTravelPolicyholder(insureds).valid;
    },
    /** Agent only — manual adult DOB when travelling party has minor(s) and no travelling adult. */
    showManualPolicyholderDob() {
      return !this.accountHolderChannel
        && needsSeparateAdultPolicyholder(this.quoteTravellersForPricing());
    },
    usesSeparatePolicyholderAtQuote() {
      const holderDob = this.effectiveAccountHolderDob;
      if (!holderDob || !isTravelAdult(holderDob)) return false;
      if (this.accountHolderChannel && !this.accountHolderTravelling) return true;
      return needsSeparateAdultPolicyholder(this.quoteTravellersForPricing());
    },
    usesAccountHolderPolicyholder() {
      return this.usesSeparatePolicyholderAtQuote;
    },
    policyholderDobReadonly() {
      return !!(this.accountHolderIprsFetched && this.accountHolderDob && isTravelAdult(this.accountHolderDob));
    },
    showSelfTravellerDob() {
      return this.accountHolderChannel && this.accountHolderTravelling && this.travellerCount >= 1;
    },
    showSingleTravellerDob() {
      return this.accountHolderChannel
        && !this.accountHolderTravelling
        && this.travellerCount === 1;
    },
    showCustomerPolicyholderDob() {
      return this.accountHolderChannel && !this.accountHolderTravelling;
    },
    showTravellerDobForm() {
      if (!this.accountHolderChannel) return false;
      if (this.accountHolderTravelling) return this.travellerCount > 1;
      return this.travellerCount > 1;
    },
    dobFormTravellers() {
      if (this.accountHolderChannel && this.accountHolderTravelling) {
        return this.quoteTravellers.slice(1);
      }
      return this.quoteTravellers;
    },
    dobFormCount() {
      if (this.accountHolderChannel && this.accountHolderTravelling) {
        return Math.max(this.travellerCount - 1, 0);
      }
      return this.travellerCount;
    },
    customerTravellerDobOffset() {
      return this.accountHolderChannel && this.accountHolderTravelling ? 1 : 0;
    },
    selfTravellerDobError() {
      if (!this.showSelfTravellerDob || this.travellerDobAt(0)) return '';
      return this.accountHolderDobError;
    },
    selfDobLockedFromIprs() {
      const t = this.quoteTravellers[0];
      return !!(t?.identityFromIprs && this.travellerDobAt(0));
    },
    effectiveAccountHolderDob() {
      if (this.accountHolderDob) return normalizeTravelDob(this.accountHolderDob);
      if (this.accountHolderChannel && this.accountHolderTravelling) {
        return this.travellerDobAt(0) || '';
      }
      return '';
    },
    travellerCountHint() {
      if (this.accountHolderChannel && this.accountHolderTravelling) {
        return 'Traveller 1 is you. Enter date of birth for each person on the trip (names and passport at sale).';
      }
      if (this.accountHolderChannel && !this.accountHolderTravelling) {
        return 'You\'re the policyholder (not travelling). Enter each traveller\'s date of birth — minors are allowed.';
      }
      if (!this.accountHolderChannel) {
        return 'Enter each traveller\'s date of birth. If any traveller is under 18, add an adult policyholder date of birth below.';
      }
      return 'Enter each traveller\'s date of birth below. Names and passport at sale.';
    },
    maxPolicyholderDob() {
      return new Date().toISOString().slice(0, 10);
    },
    canContinue() {
      return !!(
        this.canQuote
        && this.grossUsd
        && this.activeQuote?.quoteId
        && this.activeQuote?.grossUsd
        && !this.syncing
        && !this.pricing
      );
    },
    summaryLines() {
      const p = this.selectedProduct;
      if (!p) return [];
      const days = travelDaysBetween(this.startDate, this.endDate);
      const dest = this.countryOptions.find((c) => c.code === this.destinationCountry);
      return [
        { label: 'Product', value: p.label },
        { label: 'Zone', value: ZONE_LABELS[p.zone] || p.zone },
        { label: 'Destination', value: dest?.label?.split(' (')[0] || '—' },
        { label: 'Travel period', value: days ? `${days} days` : '—' },
        { label: 'Travellers', value: String(this.travellerCount) },
      ];
    },
    continueLabel() {
      if (this.guest) return 'Proceed to buy';
      if (this.channel === 'customer') return 'Continue';
      return this.journey === 'direct' ? 'Convert to sale' : 'Continue to sale';
    },
    sidebarContinueLabel() {
      if (!this.canQuote || !this.grossUsd || this.pricing) return '';
      if (this.syncing) return 'Saving quote…';
      return this.continueLabel;
    },
  },
  watch: {
    selectedProdCode() {
      this.destinationCountry = null;
      if (this.travellerCount > this.maxTravellers) {
        this.setTravellerCount(this.maxTravellers);
      }
      this.resetPrice();
    },
    quoteReadyKey: {
      handler(val, oldVal) {
        if (!val) {
          if (oldVal) this.resetPrice();
          return;
        }
        if (val !== oldVal) {
          this.resetPrice();
          this.scheduleLivePrice();
        }
      },
      immediate: true,
    },
    pricingInputsKey(val, oldVal) {
      if (val && val !== oldVal) {
        this.scheduleLivePrice();
      }
    },
    canQuote(val, oldVal) {
      if (val && !oldVal) {
        this.scheduleLivePrice();
      }
    },
    loadingAccountHolder(val, oldVal) {
      if (oldVal && !val) {
        this.scheduleLivePrice();
      }
    },
    referralAgent: {
      deep: true,
      handler(agent) {
        if (this.channel !== 'customer') return;
        if (agent) {
          this.agnShtDesc = referralAgentShortDesc(agent);
          this.agentNumericId = referralAgencyNumericId(agent);
        } else {
          this.agnShtDesc = 'DIRECT';
          this.agentNumericId = 0;
        }
        this.scheduleLivePrice();
      },
    },
  },
  async mounted() {
    if (this.travelConfig.trackAnalytics) {
      trackAgentInteraction(this, 'travel_plan_select_view', { journey: this.journey });
    }
    await this.loadTravelFxRate();
    if (this.channel === 'agent') {
      this.agnShtDesc = await agentTravelApi.resolveAgentShortCode(this.authenticatedUser);
      this.agentNumericId = await agentTravelApi.resolveAgentNumericId(this.authenticatedUser);
    } else if (this.guest) {
      this.agnShtDesc = 'DIRECT';
      this.agentNumericId = 0;
      this.accountHolderTravelling = false;
    } else {
      await this.$store.dispatch('referral/ensureReferralValidated', this.$route.query);
      if (this.hasValidReferral) {
        this.agnShtDesc = referralAgentShortDesc(this.referralAgent);
        this.agentNumericId = referralAgencyNumericId(this.referralAgent);
      } else {
        this.agnShtDesc = 'DIRECT';
        this.agentNumericId = 0;
      }
      const profile = getCognitoIdentityProfile(this.authenticatedUser);
      if (profile?.email && !this.customerEmail) this.customerEmail = profile.email;
      await this.ensureAccountHolderProfile();
    }
    const email = this.authenticatedUser?.email || (this.guest ? GUEST_SESSION_KEY : '');
    if (this.$route.query.reset === 'true') {
      clearTravelSession(email);
      this.resetFormState();
      this.$router.replace({
        name: this.guest ? GUEST_QUOTE_ROUTES.travelPlans : this.travelConfig.routes.planSelect,
        query: this.guest ? {} : { journey: this.journey },
      });
      return;
    }
    const pending = loadTravelPendingQuote(email);
    if (pending?.quoteId) {
      this.hydrateFromQuote(pending);
    }
    await this.$nextTick();
    this.scheduleLivePrice();
  },
  beforeDestroy() {
    if (this.priceTimer) clearTimeout(this.priceTimer);
  },
  methods: {
    isTravelAdult,
    isTravelMinor,
    travellerDobAt(index) {
      const dob = normalizeTravelDob(this.quoteTravellers[index]?.dateOfBirth);
      if (dob) return dob;
      if (index === 0 && this.accountHolderChannel && this.accountHolderTravelling) {
        return normalizeTravelDob(this.accountHolderDob);
      }
      return '';
    },
    inputValue(value) {
      if (value != null && typeof value === 'object' && value.target) {
        return value.target.value;
      }
      return value;
    },
    ensureTravellerRow(index, isMain = false) {
      while (this.quoteTravellers.length <= index) {
        this.quoteTravellers.push(emptyQuoteTraveller(false));
      }
      if (!this.quoteTravellers[index]) {
        this.$set(this.quoteTravellers, index, emptyQuoteTraveller(isMain));
      } else if (isMain && !this.quoteTravellers[index].isMain) {
        this.$set(this.quoteTravellers[index], 'isMain', true);
      }
    },
    onTravellerDobInput(index, value) {
      const dob = normalizeTravelDob(this.inputValue(value));
      const isSelf = this.accountHolderChannel && this.accountHolderTravelling && index === 0;
      this.ensureTravellerRow(index, isSelf || index === 0);
      if (this.quoteTravellers[index].dateOfBirth !== dob) {
        this.$set(this.quoteTravellers[index], 'dateOfBirth', dob);
        if (isSelf && dob && isTravelAdult(dob)) {
          this.accountHolderDob = dob;
          this.accountHolderDobError = '';
        }
      }
      this.scheduleLivePrice();
    },
    quoteTravellersForPricing() {
      return this.quoteTravellers.map((t, i) => ({
        ...t,
        dateOfBirth: this.travellerDobAt(i) || normalizeTravelDob(t.dateOfBirth),
      }));
    },
    syncTravellerDobsForPricing() {
      if (!this.accountHolderChannel || !this.accountHolderTravelling || !this.quoteTravellers[0]) return;
      const fromTraveller = normalizeTravelDob(this.quoteTravellers[0].dateOfBirth);
      const fromHolder = normalizeTravelDob(this.accountHolderDob);
      const dob = fromTraveller || fromHolder;
      if (!dob) return;
      if (!fromTraveller) {
        this.$set(this.quoteTravellers[0], 'dateOfBirth', dob);
      }
      if (!fromHolder && isTravelAdult(dob)) {
        this.accountHolderDob = dob;
      }
      if (isTravelAdult(dob)) {
        this.accountHolderDobError = '';
      }
    },
    goBack() {
      this.$router.push({
        name: this.guest ? GUEST_QUOTE_ROUTES.products : this.travelConfig.routes.products,
      });
    },
    hydrateFromQuote(q) {
      this.selectedProdCode = q.prodCode || this.selectedProdCode;
      this.destinationCountry = q.destinationCountry || q.destination_country;
      this.startDate = q.startDate || q.travel_start_date || '';
      this.endDate = q.endDate || q.travel_end_date || '';
      this.fxRate = q.fxRate || q.fx_rate || DEFAULT_USD_TO_KSH;
      this.customerEmail = q.customerEmail || q.email_address || '';
      this.travellerCount = Math.min(q.travellerCount || q.traveller_count || 1, this.maxTravellers);
      this.accountHolderTravelling = inferAccountHolderTravelling(q);
      this.accountHolderDob = q.accountHolderDob || '';
      this.accountHolderFirstName = q.accountHolderFirstName || '';
      this.accountHolderSurname = q.accountHolderSurname || '';
      this.hydrateQuoteTravellers(q);
      this.grossUsd = q.grossUsd || q.premium;
      this.pricingData = q.pricingData;
      this.activeQuote = q;
      if (this.accountHolderChannel && !this.accountHolderDob) {
        this.ensureAccountHolderProfile();
      }
    },
    inferCoverForFromQuote(q) {
      return coverForFromAccountHolderTravelling(inferAccountHolderTravelling(q));
    },
    resetPrice() {
      this.grossUsd = null;
      this.pricingData = null;
      this.activeQuote = {};
    },
    resetFormState() {
      if (this.priceTimer) clearTimeout(this.priceTimer);
      this.priceRequestId += 1;
      this.selectedProdCode = 'EUR_BAS';
      this.destinationCountry = null;
      this.startDate = '';
      this.endDate = '';
      this.fxRate = DEFAULT_USD_TO_KSH;
      this.fxRateError = '';
      this.travellerCount = 1;
      this.accountHolderTravelling = !this.guest;
      this.accountHolderDobError = '';
      this.quoteTravellers = [emptyQuoteTraveller(!this.guest)];
      this.customerEmail = '';
      this.lastPricingError = '';
      this.resetPrice();
      if (this.channel === 'customer' && !this.guest) {
        this.ensureAccountHolderProfile();
      }
      this.loadTravelFxRate();
    },
    async loadTravelFxRate() {
      if (!AGENT_TRAVEL_QUOTES_API_ENABLED) {
        this.fxRate = DEFAULT_USD_TO_KSH;
        return;
      }
      this.loadingFxRate = true;
      this.fxRateError = '';
      try {
        const res = await agentTravelApi.getTravelFxRate();
        const rate = Number(res.data?.rate ?? res.data?.fxRate);
        if (res.success && rate > 0) {
          this.fxRate = rate;
        } else {
          this.fxRate = DEFAULT_USD_TO_KSH;
        }
      } catch {
        this.fxRate = DEFAULT_USD_TO_KSH;
      } finally {
        this.loadingFxRate = false;
        this.scheduleLivePrice();
      }
    },
    applyFxFromApiDto(dto) {
      if (!dto) return;
      const rate = Number(dto.fxRate ?? dto.fx_rate ?? dto.rate);
      if (rate > 0) this.fxRate = rate;
    },
    startNewQuote() {
      clearTravelSession(this.authenticatedUser?.email || (this.guest ? GUEST_SESSION_KEY : ''));
      this.showNewQuoteConfirm = false;
      this.resetFormState();
      if (this.guest) {
        this.accountHolderTravelling = false;
      }
      if (this.$route.query.quoteId || this.$route.query.reset) {
        this.$router.replace({
          name: this.guest ? GUEST_QUOTE_ROUTES.travelPlans : this.travelConfig.routes.planSelect,
          query: this.guest ? {} : { journey: this.journey },
        });
      }
      if (Vue.$toast?.success) {
        Vue.$toast.success('Ready for a new travel quote.');
      }
    },
    setAccountHolderTravelling(travelling) {
      if (this.accountHolderTravelling === travelling) return;
      this.accountHolderTravelling = travelling;
      this.accountHolderDobError = '';
      this.lastPricingError = '';
      this.resetPrice();
      if (travelling) {
        this.quoteTravellers = Array.from({ length: this.travellerCount }, (_, i) => ({
          ...emptyQuoteTraveller(i === 0),
          dateOfBirth: i === 0 ? (this.accountHolderDob || '') : '',
          firstName: i === 0 ? (this.accountHolderFirstName || '') : '',
          surname: i === 0 ? (this.accountHolderSurname || '') : '',
          identityFromIprs: i === 0 && this.accountHolderIprsFetched && !!this.accountHolderDob,
        }));
      } else {
        this.quoteTravellers = Array.from({ length: this.travellerCount }, () => ({
          ...emptyQuoteTraveller(false),
          isTraveling: true,
        }));
      }
      if (this.accountHolderChannel) {
        this.ensureAccountHolderProfile();
      }
      this.scheduleLivePrice();
    },
    async ensureAccountHolderProfile({ force = false } = {}) {
      if (!this.accountHolderChannel) return;
      const profile = getCognitoIdentityProfile(this.authenticatedUser);
      if (!profile?.idNumber) {
        const msg = 'Your national ID is required on your Pacis account.';
        this.accountHolderDobError = msg;
        return;
      }
      if (!force && this.accountHolderIprsFetched && this.accountHolderDob) {
        this.applySelfTravellerFromIprs();
        this.scheduleLivePrice();
        return;
      }
      this.loadingAccountHolder = true;
      this.accountHolderDobError = '';
      try {
        const res = await fetchIprsProfile(profile.idNumber, profile.firstName);
        const iprs = res?.iprs;
        const dob = iprs?.dateOfBirth;
        if (iprs) {
          const names = applyIprsToTravellerRow(
            { firstName: '', surname: '' },
            iprs,
          );
          this.accountHolderFirstName = names.firstName;
          this.accountHolderSurname = names.surname;
          this.accountHolderIprsFetched = true;
        }
        if (dob) {
          this.accountHolderDob = dob;
          this.applySelfTravellerFromIprs();
        } else {
          this.accountHolderDobError = res?.iprsUnavailable
            ? 'Could not load your date of birth from ID records.'
            : 'Your ID records did not include a date of birth.';
          if (this.accountHolderTravelling && this.quoteTravellers[0]) {
            this.$set(this.quoteTravellers[0], 'identityFromIprs', false);
          }
        }
      } catch {
        this.accountHolderDobError = 'Could not verify your identity. Try again or contact support.';
      } finally {
        this.loadingAccountHolder = false;
        this.scheduleLivePrice();
      }
    },
    applySelfTravellerFromIprs() {
      if (!this.accountHolderChannel || !this.accountHolderTravelling) return;
      if (!this.quoteTravellers[0]) return;
      const fromIprs = this.accountHolderIprsFetched && !!this.accountHolderDob;
      if (this.accountHolderDob) {
        this.$set(this.quoteTravellers[0], 'dateOfBirth', this.accountHolderDob);
      }
      if (this.accountHolderFirstName) {
        this.$set(this.quoteTravellers[0], 'firstName', this.accountHolderFirstName);
      }
      if (this.accountHolderSurname) {
        this.$set(this.quoteTravellers[0], 'surname', this.accountHolderSurname);
      }
      this.$set(this.quoteTravellers[0], 'identityFromIprs', fromIprs);
    },
    setTravellerCount(n) {
      const next = Math.min(Math.max(Number(n) || 1, 1), this.maxTravellers);
      if (next !== this.travellerCount) {
        this.travellerCount = next;
        this.syncQuoteTravellerCount(next);
        this.scheduleLivePrice();
      }
    },
    hydrateQuoteTravellers(q) {
      const count = Math.min(Math.max(q.travellerCount || q.traveller_count || 1, 1), this.maxTravellers);
      const travelling = this.accountHolderChannel
        ? inferAccountHolderTravelling(q)
        : true;
      const saved = (q.travellers || q.quoteTravellers || []).slice(0, count);
      if (saved.length && saved.some((t) => t.dateOfBirth)) {
        this.quoteTravellers = Array.from({ length: count }, (_, i) => ({
          ...emptyQuoteTraveller(travelling && i === 0),
          dateOfBirth: saved[i]?.dateOfBirth || '',
          firstName: saved[i]?.firstName || '',
          surname: saved[i]?.surname || '',
          isMain: saved[i]?.isMain ?? (travelling && i === 0),
        }));
        this.applySelfTravellerFromIprs();
        return;
      }
      this.quoteTravellers = Array.from({ length: count }, (_, i) => (
        emptyQuoteTraveller(travelling && i === 0)
      ));
      this.applySelfTravellerFromIprs();
    },
    syncQuoteTravellerCount(count) {
      const n = Math.min(Math.max(count || 1, 1), this.maxTravellers);
      const travelling = !this.accountHolderChannel || this.accountHolderTravelling;
      const current = [...this.quoteTravellers];
      if (current.length > n) {
        this.quoteTravellers = current.slice(0, n);
      } else {
        while (this.quoteTravellers.length < n) {
          this.quoteTravellers.push(emptyQuoteTraveller(false));
        }
      }
      if (travelling && !this.quoteTravellers.some((t) => t.isMain)) {
        this.quoteTravellers[0].isMain = true;
      }
      this.applySelfTravellerFromIprs();
    },
    onDobFormTravellersUpdate(updated) {
      if (this.accountHolderChannel && this.accountHolderTravelling) {
        const lead = this.quoteTravellers[0] || emptyQuoteTraveller(true);
        this.quoteTravellers = [lead, ...updated];
      } else {
        this.onQuoteTravellersUpdate(updated);
        return;
      }
      this.scheduleLivePrice();
    },
    onQuoteTravellersUpdate(updated) {
      this.quoteTravellers = updated;
      if (this.accountHolderChannel && this.accountHolderTravelling) {
        const mainDob = normalizeTravelDob(updated[0]?.dateOfBirth);
        if (mainDob && isTravelAdult(mainDob)) {
          this.accountHolderDob = mainDob;
          this.accountHolderDobError = '';
        }
      }
      this.applySelfTravellerFromIprs();
      if (!this.accountHolderChannel && !needsSeparateAdultPolicyholder(this.quoteTravellersForPricing())) {
        this.accountHolderDob = '';
        this.accountHolderFirstName = '';
        this.accountHolderSurname = '';
        this.accountHolderDobError = '';
      }
      this.scheduleLivePrice();
    },
    onAccountHolderDobChange(value) {
      this.accountHolderDob = normalizeTravelDob(this.inputValue(value));
      this.accountHolderDobError = '';
      if (this.accountHolderDob && !isTravelAdult(this.accountHolderDob)) {
        this.accountHolderDobError = 'Policyholder must be at least 18 years old.';
      }
      this.scheduleLivePrice();
    },
    onPolicyholderError(message) {
      if (this.$toast?.warning) this.$toast.warning(message, 'Policyholder');
    },
    scheduleLivePrice() {
      if (this.priceTimer) clearTimeout(this.priceTimer);
      this.priceTimer = setTimeout(() => this.fetchLivePrice(), PRICE_DEBOUNCE_MS);
    },
    buildPlanInsureds() {
      const travellers = this.quoteTravellersForPricing();
      if (this.usesSeparatePolicyholderAtQuote) {
        return buildPlanStageInsureds({
          accountHolderTravelling: this.accountHolderChannel ? this.accountHolderTravelling : false,
          coverFor: this.accountHolderChannel && !this.accountHolderTravelling ? 'other' : undefined,
          quoteTravellers: travellers,
          accountHolderDob: this.effectiveAccountHolderDob,
        });
      }
      return buildQuoteInsureds(reconcileTravelPolicyholder(travellers));
    },
    buildPayload({ skipPolicyholderValidation = false } = {}) {
      const p = this.selectedProduct;
      const insureds = this.buildPlanInsureds();
      return buildMapfreTravelPayload({
        prodCode: p.prodCode,
        zone: p.zone,
        destinationCountry: this.destinationCountry,
        startDate: this.startDate,
        endDate: this.endDate,
        agnShtDesc: this.agnShtDesc,
        email: this.customerEmail || undefined,
        insureds,
        skipPolicyholderValidation: skipPolicyholderValidation || this.usesAccountHolderPolicyholder,
      });
    },
    async fetchLivePrice() {
      let requestId = null;
      try {
        this.syncTravellerDobsForPricing();
        await this.$nextTick();
        if (!this.canQuote) {
          return;
        }
        const insureds = this.buildPlanInsureds();
        const skipHolderCheck = this.usesAccountHolderPolicyholder;
        const holderCheck = validateTravelPolicyholder(insureds);
        if (!holderCheck.valid && !skipHolderCheck) {
          this.resetPrice();
          if (holderCheck.message !== this.lastPricingError && this.$toast?.warning) {
            this.$toast.warning(holderCheck.message, 'Policyholder');
          }
          this.lastPricingError = holderCheck.message;
          return;
        }
        this.priceRequestId += 1;
        requestId = this.priceRequestId;
        this.pricing = true;
        this.lastPricingError = '';
        if (this.travelConfig.trackAnalytics) {
          trackAgentInteraction(this, 'travel_get_price', {
            prodCode: this.selectedProdCode,
            channel: this.channel,
          });
        }
        let payload;
        try {
          payload = this.buildPayload({ skipPolicyholderValidation: skipHolderCheck });
        } catch (e) {
          this.resetPrice();
          const msg = e.message || 'Could not build pricing request.';
          if (msg !== this.lastPricingError && this.$toast?.warning) {
            this.$toast.warning(msg, 'Pricing');
          }
          this.lastPricingError = msg;
          return;
        }
        const res = await agentTravelApi.getPrice(payload);
        if (requestId !== this.priceRequestId) return;
        if (!res.success || !res.data) {
          this.resetPrice();
          const msg = res.message || 'Could not calculate premium. Check trip details.';
          if (msg !== this.lastPricingError && this.$toast?.warning) {
            this.$toast.warning(msg, 'Pricing');
          }
          this.lastPricingError = msg;
          return;
        }
        this.pricingData = res.data;
        const priceData = res.data?.data && typeof res.data.data === 'object' ? res.data.data : res.data;
        this.grossUsd = priceData?.grossPrice || priceData?.netPrice || priceData?.premium;
        if (!this.grossUsd) {
          this.resetPrice();
          const msg = res.message || 'Could not read premium from pricing response.';
          if (msg !== this.lastPricingError && this.$toast?.warning) {
            this.$toast.warning(msg, 'Pricing');
          }
          this.lastPricingError = msg;
          return;
        }
        await this.syncQuoteToDb();
      } catch (e) {
        this.resetPrice();
        const msg = e?.message || 'Could not calculate premium.';
        if (msg !== this.lastPricingError && this.$toast?.warning) {
          this.$toast.warning(msg, 'Pricing');
        }
        this.lastPricingError = msg;
      } finally {
        if (requestId !== null && requestId === this.priceRequestId) {
          this.pricing = false;
        }
      }
    },
    async syncQuoteToDb() {
      if (!this.grossUsd || !this.pricingData) return;
      this.syncing = true;
      try {
        const p = this.selectedProduct;
        const duration = durationLabelFromDates(this.startDate, this.endDate);
        const accountIdentity = getCognitoIdentityProfile(this.authenticatedUser);
        const mainDob = this.quoteAccountHolderTravelling
          ? (this.quoteTravellers.find((t) => t.isMain)?.dateOfBirth || this.quoteTravellers[0]?.dateOfBirth)
          : (this.effectiveAccountHolderDob || this.quoteTravellers[0]?.dateOfBirth);
        const quoteData = {
          plan_type: p.planType,
          region: p.region,
          zone: p.zone,
          duration,
          birth_date: mainDob || undefined,
          quoteTravellersJson: JSON.stringify(this.quoteTravellers),
          email_address: this.customerEmail || accountIdentity?.email || undefined,
          client_full_name: accountIdentity?.fullName || undefined,
          destination_country: this.destinationCountry,
          travel_start_date: this.startDate,
          travel_end_date: this.endDate,
          prod_code: p.prodCode,
          premium: this.grossUsd,
          revised_price: this.grossUsd,
          traveller_count: this.travellerCount,
          agent_journey: this.journey,
          agnShtDesc: this.agnShtDesc,
        };

        let quoteId = this.activeQuote?.quoteId;
        let quoteLocalOnly = !!this.activeQuote?.quoteLocalOnly;
        if (quoteId && (!isRemoteTravelQuoteId(quoteId) || quoteLocalOnly)) {
          quoteId = null;
          quoteLocalOnly = false;
        }
        let serverFxRate = this.fxRate;
        let serverKshAmount = usdToKsh(this.grossUsd, this.fxRate);
        if (!quoteId) {
          const agentIdForCreate = this.channel === 'customer' && !this.agentNumericId
            ? null
            : this.agentNumericId;
          const created = await agentTravelApi.createQuote(quoteData, agentIdForCreate);
          if (created.success && created.data?.id) {
            quoteId = created.data.id;
            quoteLocalOnly = !!created.local;
            this.applyFxFromApiDto(created.data);
            serverFxRate = this.fxRate;
            serverKshAmount = Math.ceil(Number(created.data.kshAmount ?? created.data.ksh_amount) || serverKshAmount);
          } else if (this.channel === 'customer') {
            quoteId = generateLocalTravelQuoteId();
            quoteLocalOnly = true;
            if (this.$toast?.warning) {
              this.$toast.warning(
                created.message || 'Quote saved in this session only. You can still continue.',
                'Quote',
              );
            }
          } else {
            if (this.$toast?.warning) {
              this.$toast.warning(created.message || 'Could not save quote. Try again.', 'Quote');
            }
            return;
          }
        }

        if (!quoteLocalOnly) {
          const pricingRes = await agentTravelApi.updateQuotePricing(quoteId, this.pricingData);
          if (pricingRes.success && pricingRes.data) {
            this.applyFxFromApiDto(pricingRes.data);
            serverFxRate = this.fxRate;
            serverKshAmount = Math.ceil(Number(pricingRes.data.kshAmount ?? pricingRes.data.ksh_amount) || serverKshAmount);
          } else if (!pricingRes.success && !pricingRes.skipped && this.$toast?.warning) {
            this.$toast.warning('Quote saved but pricing sync failed', 'Notice');
          }
        }

        let mapfrePayload;
        try {
          mapfrePayload = {
            ...this.buildPayload({ skipPolicyholderValidation: this.usesAccountHolderPolicyholder }),
            premium: String(this.grossUsd),
            email: this.customerEmail || undefined,
          };
        } catch (e) {
          if (this.$toast?.warning) {
            this.$toast.warning(e.message || 'Could not build quote details', 'Quote');
          }
          return;
        }
        const snapshot = {
          quoteId,
          prodCode: p.prodCode,
          region: p.region,
          zone: p.zone,
          planType: p.planType,
          productLabel: p.label,
          destinationCountry: this.destinationCountry,
          startDate: this.startDate,
          endDate: this.endDate,
          fxRate: serverFxRate,
          grossUsd: this.grossUsd,
          kshAmount: serverKshAmount,
          travellerCount: this.travellerCount,
          accountHolderTravelling: this.quoteAccountHolderTravelling,
          coverFor: coverForFromAccountHolderTravelling(this.quoteAccountHolderTravelling),
          accountHolderDob: this.accountHolderDob || undefined,
          accountHolderFirstName: this.accountHolderFirstName || undefined,
          accountHolderSurname: this.accountHolderSurname || undefined,
          customerEmail: this.customerEmail,
          travellers: JSON.parse(JSON.stringify(this.quoteTravellers)),
          quoteTravellerDobs: this.quoteTravellers.map((t) => t.dateOfBirth),
          agnShtDesc: this.agnShtDesc,
          pricingData: this.pricingData,
          mapfrePayload,
          summaryLines: this.summaryLines,
          quoteLocalOnly,
        };
        this.activeQuote = snapshot;
        saveTravelPendingQuote(
          this.authenticatedUser?.email || (this.guest ? GUEST_SESSION_KEY : ''),
          snapshot,
        );
      } finally {
        this.syncing = false;
      }
    },
    async goToSale() {
      if (this.syncing) {
        if (this.$toast?.warning) {
          this.$toast.warning('Quote is still saving — wait a moment and try again.');
        }
        return;
      }
      await this.syncQuoteToDb();
      const holderCheck = validateTravelPolicyholder(this.buildPlanInsureds());
      if (!holderCheck.valid && !this.usesAccountHolderPolicyholder) {
        if (this.$toast?.warning) this.$toast.warning(holderCheck.message, 'Policyholder');
        return;
      }
      if (!this.activeQuote?.quoteId || !this.activeQuote?.grossUsd) {
        if (this.$toast?.warning) {
          this.$toast.warning('Wait for the live premium to finish saving, then try again.');
        }
        return;
      }
      if (this.travelConfig.trackAnalytics) {
        trackAgentInteraction(this, 'travel_continue_sale', { quoteId: this.activeQuote.quoteId });
      }
      const buyRoute = guestBuyRouteForProduct('travel', {
        quoteId: String(this.activeQuote.quoteId),
        journey: this.journey,
      });
      if (this.guest) {
        redirectGuestToBuy(this.$router, buyRoute);
        return;
      }
      this.$router.push({
        name: this.travelConfig.routes.sale,
        query: { quoteId: String(this.activeQuote.quoteId), journey: this.journey },
      });
    },
  },
  metaInfo: { title: 'Travel — Get Quote' },
};
</script>

<style scoped lang="scss">
@import '@/styles/agent-pa-form.scss';

.travel-body { padding: 20px 32px; }
.travel-header {
  max-width: 1100px;
  margin: 0 auto 20px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.travel-header-left { flex: 1; min-width: 0; }
.btn-new-sale {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid #CBD5E1;
  background: #F8FAFC;
  color: #1E4276;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
}
.btn-new-sale:hover { background: #EEF2FF; }
.new-sale-dialog {
  background: white;
  border-radius: 14px;
  box-shadow: 0 20px 40px rgba(2, 6, 23, 0.25);
  overflow: hidden;
}
.nsd-header {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 16px 16px 10px;
  border-bottom: 1px solid #E2E8F0;
}
.nsd-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #f59e0b, #ef4444);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.nsd-title { margin: 0; font-size: 1rem; font-weight: 700; color: #0F172A; }
.nsd-sub { margin: 2px 0 0; font-size: 0.8rem; color: #64748B; }
.nsd-body { padding: 12px 16px; }
.nsd-desc { margin: 0; font-size: 0.82rem; color: #334155; line-height: 1.5; }
.nsd-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 16px 16px;
}
.nsd-btn-cancel, .nsd-btn-confirm {
  border: none;
  border-radius: 9px;
  padding: 10px 14px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
}
.nsd-btn-cancel { background: #F1F5F9; color: #334155; }
.nsd-btn-cancel:hover { background: #E2E8F0; }
.nsd-btn-confirm {
  background: linear-gradient(135deg, #00AEEF, #1E4276);
  color: white;
}
.nsd-btn-confirm:hover { filter: brightness(0.95); }
.page-title {
  display: flex; align-items: center; font-size: 1.25rem; font-weight: 700;
  color: #1E4276; margin: 0 0 4px;
}
.page-sub { margin: 0; font-size: 0.85rem; color: #64748B; padding-left: 36px; }
.quote-container { max-width: 1100px; margin: 0 auto; }
.quote-layout {
  display: grid; grid-template-columns: 1fr 340px; gap: 20px; align-items: start;
}
.quote-form-column { display: flex; flex-direction: column; gap: 16px; }
.section-card {
  background: white; border-radius: 10px; box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  padding: 18px 20px; border: 1px solid #E8EDF2;
}
.section-header { display: flex; align-items: center; margin-bottom: 14px; }
.section-number {
  display: inline-flex; align-items: center; justify-content: center;
  width: 24px; height: 24px; border-radius: 50%; background: #00AEEF;
  color: white; font-weight: 700; font-size: 0.75rem; margin-right: 8px;
}
.section-header h3 { margin: 0; font-size: 1rem; color: #1E4276; font-weight: 700; }
.optional { font-weight: 400; color: #94A3B8; }
.field-error { color: #DC2626; font-size: 0.8rem; margin: 8px 0 0; }
.cover-for-block { margin-bottom: 4px; }
.cover-for-options {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.cover-for-option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  border: 1.5px solid #E2E8F0;
  background: #F8FAFC;
  color: #475569;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.cover-for-option:hover { border-color: #CBD5E1; background: #F1F5F9; }
.cover-for-option.active {
  border-color: #00AEEF;
  background: #E0F2FE;
  color: #1E4276;
}
.policyholder-dob-block {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #E8EDF2;
}

.req { color: #DC2626; }

.field-hint { font-size: 0.75rem; color: #94A3B8; margin: 0 0 8px; }
.family-plan-note {
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #E0F2FE;
  border: 1px solid #BAE6FD;
  color: #1E4276;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.4;
}
.traveller-stepper {
  display: inline-flex; align-items: center; gap: 0;
  border: 1px solid #E2E8F0; border-radius: 10px; overflow: hidden; background: #fff;
}
.stepper-btn {
  width: 44px; height: 44px; border: none; background: #F8FAFC; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.stepper-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.stepper-btn:not(:disabled):hover { background: #E0F2FE; }
.stepper-value {
  min-width: 48px; text-align: center; font-size: 1.1rem; font-weight: 800; color: #1E4276;
  padding: 0 12px; border-left: 1px solid #E2E8F0; border-right: 1px solid #E2E8F0;
}
.self-dob-card {
  border: 1px solid #E8EDF2;
  border-radius: 10px;
  padding: 12px 14px 4px;
  margin-bottom: 10px;
  background: #FAFCFE;
}
.self-dob-card .dob-card-top { margin-bottom: 8px; }
.self-dob-card .traveller-badge {
  font-size: 0.72rem;
  font-weight: 800;
  color: #1E4276;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #E0F2FE;
  padding: 4px 10px;
  border-radius: 20px;
  display: inline-block;
}
.quote-sidebar { position: sticky; top: 20px; }
@media (max-width: 960px) {
  .travel-body { padding: 16px 16px 28px; }
  .quote-layout { grid-template-columns: 1fr; gap: 16px; }
  .quote-sidebar { position: static; }
  .section-card { padding: 16px; }
}
@media (max-width: 600px) {
  .travel-body { padding: 12px 12px 88px; }
  .travel-header {
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
  }
  .page-title { font-size: 1.15rem; line-height: 1.3; }
  .nsd-footer {
    flex-direction: column-reverse;
    gap: 8px;
  }
  .nsd-footer > * {
    width: 100%;
    justify-content: center;
  }
}
</style>
